// src/ui/IdentityMatrixDialog.js
// Professional Identity Matrix Update Dialog - Fortune 500 Grade
// Complete enterprise implementation with audit trails, rate limiting, and accessibility

import { auth, db, firebase } from '../firebaseConfig.js';

export default class IdentityMatrixDialog {
    constructor(profileScene) {
        this.profileScene = profileScene;
        this.dialogId = 'identity-matrix-dialog';
        this.originalValues = {};
        this.currentValues = {};
        this.auditHistory = [];
        this.rateLimitData = null;
        this.isDraftSaved = false;
        this.validationRules = {
            displayName: {
                required: true,
                minLength: 2,
                maxLength: 50,
                pattern: /^[a-zA-Z\s\-'\.]+$/,
                message: 'Only letters, spaces, hyphens, apostrophes, and periods allowed'
            },
            professionalTitle: {
                required: false,
                maxLength: 100,
                pattern: /^[a-zA-Z\s\-'\.&,()]+$/,
                message: 'Professional title contains invalid characters'
            },
            department: {
                required: false,
                maxLength: 100,
                pattern: /^[a-zA-Z\s\-'\.&,()]+$/,
                message: 'Department contains invalid characters'
            }
        };
        
        // Enterprise content filtering
        this.inappropriateWords = [
            'admin', 'test', 'root', 'user', 'guest', 'demo', 'temp', 'fake',
            'anonymous', 'unknown', 'null', 'undefined', 'system', 'bot'
        ];
        
        console.log('📊 Identity Matrix Dialog initialized with enterprise features');
    }

    async show() {
        console.log('📊 Creating professional Identity Matrix Update dialog');
        
        try {
            // Remove existing dialog
            this.hide();
            
            // Load comprehensive user data
            await this.loadUserData();
            
            // Check rate limiting
            await this.checkRateLimit();
            
            // Load audit history
            await this.loadAuditHistory();
            
            // Load any saved drafts
            this.loadDraftData();
        
            // Create dialog
            const overlay = document.createElement('div');
            overlay.id = this.dialogId;
            overlay.className = 'dialog-overlay';
            overlay.setAttribute('role', 'dialog');
            overlay.setAttribute('aria-labelledby', 'dialog-title');
            overlay.setAttribute('aria-describedby', 'dialog-subtitle');
            overlay.innerHTML = this.getDialogHTML();
            
            // Add styles
            this.addDialogStyles();
            
            // Add to DOM
            document.body.appendChild(overlay);
            
            // Setup comprehensive functionality
            this.setupEventListeners();
            this.setupAccessibility();
            this.setupFormValidation();
            this.setupDraftSaving();
            this.initializeFormState();
            
            // Show with animation
            requestAnimationFrame(() => {
                overlay.classList.add('active');
                // Focus first input for accessibility
                const firstInput = overlay.querySelector('.form-input');
                if (firstInput) firstInput.focus();
            });
            
            console.log('📊 Enterprise Identity Matrix Update dialog created with full functionality');
            
        } catch (error) {
            console.error('Error creating Identity Matrix dialog:', error);
            this.showErrorNotification('Unable to open Identity Matrix Update. Please try again.');
        }
    }
    
    async loadUserData() {
        const currentData = this.profileScene.currentData || {};
        const profileData = currentData.profileData || {};
        const firmInfo = currentData.companyEmailStatus || {};
        
        this.originalValues = {
            displayName: profileData.displayName || 'Your Name',
            professionalTitle: profileData.professionalTitle || '',
            department: profileData.department || ''
        };
        
        this.currentValues = { ...this.originalValues };
        this.firmInfo = firmInfo;
        this.lastUpdated = profileData.lastUpdated ? 
            new Date(profileData.lastUpdated).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric'
            }) : 'Never';
    }
    
    async checkRateLimit() {
        const user = auth.currentUser;
        if (!user) return;
        
        try {
            // Check last update timestamp from Firebase
            const profileDoc = await db.collection('userProfiles').doc(user.uid).get();
            if (profileDoc.exists) {
                const data = profileDoc.data();
                const lastUpdate = data.lastIdentityUpdate?.toDate();
                
                if (lastUpdate) {
                    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
                    const isRateLimited = lastUpdate > twentyFourHoursAgo;
                    
                    this.rateLimitData = {
                        isLimited: isRateLimited,
                        lastUpdate: lastUpdate,
                        canUpdateAt: isRateLimited ? 
                            new Date(lastUpdate.getTime() + 24 * 60 * 60 * 1000) : null
                    };
                } else {
                    this.rateLimitData = { isLimited: false, lastUpdate: null, canUpdateAt: null };
                }
            }
        } catch (error) {
            console.error('Error checking rate limit:', error);
            this.rateLimitData = { isLimited: false, lastUpdate: null, canUpdateAt: null };
        }
    }
    
    async loadAuditHistory() {
        const user = auth.currentUser;
        if (!user) return;
        
        try {
            // Load identity update audit trail
            const auditSnapshot = await db.collection('identityAudit')
                .where('userId', '==', user.uid)
                .orderBy('timestamp', 'desc')
                .limit(10)
                .get();
                
            this.auditHistory = [];
            auditSnapshot.forEach(doc => {
                this.auditHistory.push({
                    id: doc.id,
                    ...doc.data(),
                    timestamp: doc.data().timestamp?.toDate()
                });
            });
        } catch (error) {
            console.warn('Error loading audit history:', error);
            this.auditHistory = [];
        }
    }
    
    loadDraftData() {
        // Load any saved drafts from localStorage
        const user = auth.currentUser;
        if (!user) return;
        
        const draftKey = `identityDraft_${user.uid}`;
        const savedDraft = localStorage.getItem(draftKey);
        
        if (savedDraft) {
            try {
                const draft = JSON.parse(savedDraft);
                this.currentValues = { ...this.originalValues, ...draft };
                this.isDraftSaved = true;
                console.log('📊 Loaded saved draft for identity update');
            } catch (error) {
                console.warn('Error loading draft:', error);
            }
        }
    }

    hide() {
        const existingDialog = document.getElementById(this.dialogId);
        if (existingDialog) {
            existingDialog.remove();
        }
    }

    getDialogHTML() {
        const rateLimitWarning = this.rateLimitData?.isLimited ? 
            `<div class="rate-limit-error">
                <span>🚫</span>
                <div>
                    <strong>Update Restricted:</strong> You can update your identity once per 24 hours. 
                    Next update available: ${this.rateLimitData.canUpdateAt?.toLocaleString()}
                </div>
            </div>` : 
            `<div class="rate-limit-warning">
                <span>⚠️</span>
                <div>
                    <strong>Daily Update Limit:</strong> You can update your identity information once per day for security purposes.
                </div>
            </div>`;
            
        const auditHistoryHTML = this.auditHistory.length > 0 ? 
            `<div class="audit-history">
                <h3><span>📋</span> Recent Changes</h3>
                ${this.auditHistory.slice(0, 3).map(entry => `
                    <div class="audit-item">
                        <div class="audit-change">${entry.changeDescription}</div>
                        <div class="audit-date">${entry.timestamp?.toLocaleDateString() || 'Unknown'}</div>
                    </div>
                `).join('')}
            </div>` : '';
            
        const draftNotice = this.isDraftSaved ? 
            `<div class="draft-notice">
                <span>💾</span>
                <span>Draft changes restored from previous session</span>
            </div>` : '';
        
        return `
            <div class="identity-dialog">
                <div class="dialog-header">
                    <button class="close-btn" aria-label="Close dialog" onclick="document.getElementById('${this.dialogId}').remove()">&times;</button>
                    <div class="dialog-logo">DeckOps.ai</div>
                    <h2 class="dialog-title" id="dialog-title">Identity Matrix Update</h2>
                    <p class="dialog-subtitle" id="dialog-subtitle">Update your professional display name and profile information</p>
                </div>

                <div class="dialog-content">
                    ${draftNotice}
                    
                    <!-- Current Information Display -->
                    <div class="current-info">
                        <h3>
                            <span>👤</span>
                            Current Information
                        </h3>
                        <div class="info-grid">
                            <div class="info-item">
                                <span class="info-label">Display Name</span>
                                <span class="info-value">${this.originalValues.displayName}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Professional Title</span>
                                <span class="info-value">${this.originalValues.professionalTitle || 'Not specified'}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Department</span>
                                <span class="info-value">${this.originalValues.department || 'Not specified'}</span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Firm Affiliation</span>
                                <span class="info-value">
                                    ${this.firmInfo.verified ? this.firmInfo.name || 'Verified Firm' : 'No Firm Verification'}
                                    ${this.firmInfo.verified ? '<span class="verified-badge"><span>✓</span><span>Verified</span></span>' : ''}
                                </span>
                            </div>
                            <div class="info-item">
                                <span class="info-label">Last Updated</span>
                                <span class="info-value">${this.lastUpdated}</span>
                            </div>
                        </div>
                    </div>

                    ${auditHistoryHTML}

                    <!-- Rate Limit Check -->
                    ${rateLimitWarning}

                    <!-- Editable Fields Form -->
                    <div class="form-section">
                        <h3 class="section-title">
                            <span>✏️</span>
                            Update Information
                        </h3>

                                                    <div class="form-group">
                                <label class="form-label" for="newDisplayName">
                                    Display Name<span class="required">*</span>
                                </label>
                                <div class="form-input-container">
                                    <input 
                                        type="text" 
                                        class="form-input" 
                                        id="newDisplayName"
                                        name="displayName"
                                        value="${this.currentValues.displayName}"
                                        placeholder="Enter your professional display name"
                                        maxlength="50"
                                        aria-required="true"
                                        aria-describedby="nameValidation nameCharCount"
                                        ${this.rateLimitData?.isLimited ? 'disabled' : ''}
                                    >
                                </div>
                                <div class="char-counter">
                                    <div class="validation-message" id="nameValidation" role="alert"></div>
                                    <div class="char-count" id="nameCharCount">${this.currentValues.displayName.length} / 50</div>
                                </div>
                            </div>

                            <div class="form-group">
                                <label class="form-label" for="professionalTitle">Professional Title</label>
                                <div class="form-input-container">
                                    <input 
                                        type="text" 
                                        class="form-input" 
                                        id="professionalTitle"
                                        name="professionalTitle"
                                        value="${this.currentValues.professionalTitle}"
                                        placeholder="e.g., Senior Associate, Principal, Partner"
                                        maxlength="100"
                                        aria-describedby="titleValidation titleCharCount"
                                        ${this.rateLimitData?.isLimited ? 'disabled' : ''}
                                    >
                                </div>
                                <div class="char-counter">
                                    <div class="validation-message" id="titleValidation" role="alert"></div>
                                    <div class="char-count" id="titleCharCount">${this.currentValues.professionalTitle.length} / 100</div>
                                </div>
                            </div>

                            <div class="form-group">
                                <label class="form-label" for="department">Department/Division</label>
                                <div class="form-input-container">
                                    <input 
                                        type="text" 
                                        class="form-input" 
                                        id="department"
                                        name="department"
                                        value="${this.currentValues.department}"
                                        placeholder="e.g., Strategy & Operations, Digital Practice"
                                        maxlength="100"
                                        aria-describedby="deptValidation deptCharCount"
                                        ${this.rateLimitData?.isLimited ? 'disabled' : ''}
                                    >
                                </div>
                                <div class="char-counter">
                                    <div class="validation-message" id="deptValidation" role="alert"></div>
                                    <div class="char-count" id="deptCharCount">${this.currentValues.department.length} / 100</div>
                                </div>
                            </div>
                            
                            <!-- Preview Section -->
                            <div class="preview-section">
                                <h3><span>👁️</span> Preview</h3>
                                <div class="preview-item">
                                    <div class="preview-label">Leaderboard Display</div>
                                    <div class="preview-value" id="leaderboardPreview">${this.currentValues.displayName}</div>
                                </div>
                                <div class="preview-item">
                                    <div class="preview-label">Analytics Reports</div>
                                    <div class="preview-value" id="analyticsPreview">${this.currentValues.displayName}${this.currentValues.professionalTitle ? ' • ' + this.currentValues.professionalTitle : ''}</div>
                                </div>
                            </div>

                        <div class="form-group">
                            <label class="form-label">Professional Title</label>
                            <div class="form-input-container">
                                <input 
                                    type="text" 
                                    class="form-input" 
                                    id="professionalTitle"
                                    value="${values.professionalTitle}"
                                    placeholder="e.g., Senior Associate, Principal, Partner"
                                    maxlength="100"
                                >
                            </div>
                            <div class="char-counter">
                                <div class="validation-message" id="titleValidation"></div>
                                <div class="char-count" id="titleCharCount">${values.professionalTitle.length} / 100</div>
                            </div>
                        </div>

                        <div class="form-group">
                            <label class="form-label">Department/Division</label>
                            <div class="form-input-container">
                                <input 
                                    type="text" 
                                    class="form-input" 
                                    id="department"
                                    value="${values.department}"
                                    placeholder="e.g., Strategy & Operations, Digital Practice"
                                    maxlength="100"
                                >
                            </div>
                            <div class="char-counter">
                                <div class="validation-message" id="deptValidation"></div>
                                <div class="char-count" id="deptCharCount">${values.department.length} / 100</div>
                            </div>
                        </div>
                    </div>

                    <!-- Action Buttons -->
                    <div class="button-group">
                        <button class="btn-secondary" onclick="document.getElementById('${this.dialogId}').remove()" ${this.rateLimitData?.isLimited ? 'disabled' : ''}>
                            ${this.isDraftSaved ? 'Discard Draft' : 'Cancel'}
                        </button>
                        <button class="btn-primary" id="updateBtn" disabled ${this.rateLimitData?.isLimited ? 'style="display: none;"' : ''}>
                            Update Profile
                        </button>
                    </div>
                </div>
            </div>
        `;
    }
    
    setupAccessibility() {
        const dialog = document.getElementById(this.dialogId);
        if (!dialog) return;
        
        // Trap focus within dialog
        const focusableElements = dialog.querySelectorAll(
            'button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])'
        );
        
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];
        
        dialog.addEventListener('keydown', (e) => {
            if (e.key === 'Tab') {
                if (e.shiftKey) {
                    if (document.activeElement === firstFocusable) {
                        e.preventDefault();
                        lastFocusable.focus();
                    }
                } else {
                    if (document.activeElement === lastFocusable) {
                        e.preventDefault();
                        firstFocusable.focus();
                    }
                }
            }
        });
        
        // Announce dialog to screen readers
        const announcement = document.createElement('div');
        announcement.setAttribute('aria-live', 'polite');
        announcement.setAttribute('aria-atomic', 'true');
        announcement.className = 'sr-only';
        announcement.textContent = 'Identity Matrix Update dialog opened. Use Tab to navigate, Escape to close.';
        dialog.appendChild(announcement);
    }
    
    setupFormValidation() {
        // Setup real-time validation for all inputs
        ['newDisplayName', 'professionalTitle', 'department'].forEach(fieldId => {
            const input = document.getElementById(fieldId);
            if (input) {
                // Debounced validation for performance
                let validationTimeout;
                const debouncedValidation = () => {
                    clearTimeout(validationTimeout);
                    validationTimeout = setTimeout(() => {
                        this.validateField(fieldId);
                        this.updatePreview();
                        this.saveDraft();
                    }, 300);
                };
                
                input.addEventListener('input', debouncedValidation);
                input.addEventListener('blur', () => this.validateField(fieldId));
            }
        });
    }
    
    setupDraftSaving() {
        // Auto-save drafts every 10 seconds
        this.draftSaveInterval = setInterval(() => {
            this.saveDraft();
        }, 10000);
        
        // Save draft on page unload
        window.addEventListener('beforeunload', () => {
            this.saveDraft();
        });
    }
    
    initializeFormState() {
        // Initialize character counts and validation for pre-filled values
        ['newDisplayName', 'professionalTitle', 'department'].forEach(fieldId => {
            const input = document.getElementById(fieldId);
            if (input && input.value) {
                this.updateCharacterCount(fieldId, input.value);
                this.validateField(fieldId);
            }
        });
        
        // Initialize preview
        this.updatePreview();
        
        // Initialize button state
        this.updateButtonState();
    }
    
    updateCharacterCount(fieldId, value) {
        const charCountId = this.getCharCountId(fieldId);
        const charCountEl = document.getElementById(charCountId);
        
        if (charCountEl) {
            const limit = this.getFieldLimit(fieldId);
            const count = value.length;
            
            charCountEl.textContent = `${count} / ${limit}`;
            charCountEl.className = 'char-count';
            
            if (count > limit * 0.9) charCountEl.classList.add('warning');
            if (count > limit) charCountEl.classList.add('error');
        }
    }
    
    getCharCountId(fieldId) {
        const mapping = {
            'newDisplayName': 'nameCharCount',
            'professionalTitle': 'titleCharCount',
            'department': 'deptCharCount'
        };
        return mapping[fieldId] || fieldId + 'CharCount';
    }
    
    getFieldLimit(fieldId) {
        return fieldId === 'newDisplayName' ? 50 : 100;
    }
    
    updatePreview() {
        const displayName = document.getElementById('newDisplayName')?.value || this.originalValues.displayName;
        const title = document.getElementById('professionalTitle')?.value || '';
        const department = document.getElementById('department')?.value || '';
        
        // Update leaderboard preview
        const leaderboardPreview = document.getElementById('leaderboardPreview');
        if (leaderboardPreview) {
            leaderboardPreview.textContent = displayName || 'Your Name';
        }
        
        // Update analytics preview
        const analyticsPreview = document.getElementById('analyticsPreview');
        if (analyticsPreview) {
            let previewText = displayName || 'Your Name';
            if (title) previewText += ` • ${title}`;
            analyticsPreview.textContent = previewText;
        }
    }
    
    saveDraft() {
        const user = auth.currentUser;
        if (!user) return;
        
        const currentFormData = {
            displayName: document.getElementById('newDisplayName')?.value || '',
            professionalTitle: document.getElementById('professionalTitle')?.value || '',
            department: document.getElementById('department')?.value || '',
            timestamp: Date.now()
        };
        
        // Only save if there are actual changes
        const hasChanges = Object.keys(currentFormData).some(key => 
            key !== 'timestamp' && currentFormData[key] !== this.originalValues[key]
        );
        
        if (hasChanges) {
            const draftKey = `identityDraft_${user.uid}`;
            localStorage.setItem(draftKey, JSON.stringify(currentFormData));
            console.log('📊 Identity draft saved');
        }
    }
    
    clearDraft() {
        const user = auth.currentUser;
        if (!user) return;
        
        const draftKey = `identityDraft_${user.uid}`;
        localStorage.removeItem(draftKey);
        
        if (this.draftSaveInterval) {
            clearInterval(this.draftSaveInterval);
        }
    }

    addDialogStyles() {
        if (document.getElementById('identity-matrix-styles')) return;
        
        const style = document.createElement('style');
        style.id = 'identity-matrix-styles';
        style.textContent = `
            /* Identity Matrix Dialog Styles - Fortune 500 Grade */
            .dialog-overlay {
                position: fixed !important;
                top: 0 !important;
                left: 0 !important;
                width: 100% !important;
                height: 100% !important;
                background: rgba(0, 0, 0, 0.85) !important;
                backdrop-filter: blur(15px) !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
                z-index: 60000 !important;
                opacity: 0 !important;
                visibility: hidden !important;
                transition: all 0.4s ease !important;
            }

            .dialog-overlay.active {
                opacity: 1 !important;
                visibility: visible !important;
            }

            .identity-dialog {
                background: rgba(255, 255, 255, 0.08) !important;
                backdrop-filter: blur(25px) !important;
                border: 1px solid rgba(255, 255, 255, 0.15) !important;
                border-radius: 24px !important;
                padding: 0 !important;
                max-width: 640px !important;
                width: 90% !important;
                max-height: 90vh !important;
                overflow-y: auto !important;
                transform: translateY(30px) scale(0.95) !important;
                transition: all 0.4s ease !important;
                box-shadow: 0 30px 80px rgba(0, 0, 0, 0.5), 0 0 60px rgba(0, 212, 255, 0.1) !important;
                position: relative !important;
            }

            .dialog-overlay.active .identity-dialog {
                transform: translateY(0) scale(1) !important;
            }

            .dialog-header {
                background: linear-gradient(135deg, #1e293b 0%, #334155 100%) !important;
                padding: 36px 40px !important;
                text-align: center !important;
                position: relative !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
            }

            .close-btn {
                position: absolute !important;
                top: 20px !important;
                right: 20px !important;
                background: none !important;
                border: none !important;
                color: rgba(255, 255, 255, 0.6) !important;
                font-size: 24px !important;
                cursor: pointer !important;
                padding: 8px !important;
                border-radius: 50% !important;
                transition: all 0.3s ease !important;
                width: 40px !important;
                height: 40px !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
            }

            .close-btn:hover {
                background: rgba(255, 255, 255, 0.1) !important;
                color: white !important;
                transform: scale(1.1) !important;
            }

            .dialog-logo {
                font-size: 32px !important;
                font-weight: 800 !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 50%, #ffffff 100%) !important;
                background-clip: text !important;
                -webkit-background-clip: text !important;
                -webkit-text-fill-color: transparent !important;
                margin-bottom: 12px !important;
                letter-spacing: -1px !important;
            }

            .dialog-title {
                font-size: 26px !important;
                font-weight: 700 !important;
                color: white !important;
                margin-bottom: 8px !important;
                line-height: 1.2 !important;
            }

            .dialog-subtitle {
                font-size: 16px !important;
                color: rgba(255, 255, 255, 0.8) !important;
                line-height: 1.4 !important;
            }

            .dialog-content {
                padding: 40px !important;
            }

            .current-info {
                background: rgba(255, 255, 255, 0.05) !important;
                border: 1px solid rgba(255, 255, 255, 0.1) !important;
                border-radius: 16px !important;
                padding: 24px !important;
                margin-bottom: 32px !important;
            }

            .current-info h3 {
                color: #00d4ff !important;
                font-size: 16px !important;
                font-weight: 700 !important;
                margin-bottom: 16px !important;
                display: flex !important;
                align-items: center !important;
                gap: 8px !important;
            }

            .info-grid {
                display: grid !important;
                gap: 16px !important;
            }

            .info-item {
                display: flex !important;
                justify-content: space-between !important;
                align-items: center !important;
                padding: 12px 0 !important;
                border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
            }

            .info-item:last-child {
                border-bottom: none !important;
            }

            .info-label {
                color: rgba(255, 255, 255, 0.7) !important;
                font-size: 14px !important;
                font-weight: 500 !important;
            }

            .info-value {
                color: white !important;
                font-weight: 600 !important;
                text-align: right !important;
            }

            .verified-badge {
                display: inline-flex !important;
                align-items: center !important;
                gap: 4px !important;
                background: rgba(16, 185, 129, 0.2) !important;
                color: #10b981 !important;
                border: 1px solid rgba(16, 185, 129, 0.3) !important;
                padding: 4px 8px !important;
                border-radius: 12px !important;
                font-size: 11px !important;
                font-weight: 600 !important;
                text-transform: uppercase !important;
            }

            .form-section {
                margin-bottom: 32px !important;
            }

            .section-title {
                color: white !important;
                font-size: 18px !important;
                font-weight: 700 !important;
                margin-bottom: 20px !important;
                display: flex !important;
                align-items: center !important;
                gap: 8px !important;
            }

            .form-group {
                margin-bottom: 24px !important;
            }

            .form-label {
                display: block !important;
                font-size: 14px !important;
                font-weight: 600 !important;
                color: rgba(255, 255, 255, 0.9) !important;
                margin-bottom: 8px !important;
            }

            .form-label .required {
                color: #ef4444 !important;
                margin-left: 4px !important;
            }

            .form-input {
                width: 100% !important;
                padding: 16px 20px !important;
                background: rgba(255, 255, 255, 0.08) !important;
                border: 2px solid rgba(255, 255, 255, 0.15) !important;
                border-radius: 12px !important;
                color: white !important;
                font-size: 16px !important;
                transition: all 0.3s ease !important;
                outline: none !important;
                font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif !important;
                box-sizing: border-box !important;
            }

            .form-input:focus {
                border-color: #00d4ff !important;
                background: rgba(255, 255, 255, 0.12) !important;
                box-shadow: 0 0 20px rgba(0, 212, 255, 0.2) !important;
            }

            .form-input::placeholder {
                color: rgba(255, 255, 255, 0.5) !important;
            }

            .form-input.error {
                border-color: #ef4444 !important;
                background: rgba(239, 68, 68, 0.1) !important;
            }

            .form-input.success {
                border-color: #10b981 !important;
                background: rgba(16, 185, 129, 0.1) !important;
            }

            .char-counter {
                display: flex !important;
                justify-content: space-between !important;
                align-items: center !important;
                margin-top: 6px !important;
                font-size: 12px !important;
            }

            .char-count {
                color: rgba(255, 255, 255, 0.6) !important;
            }

            .char-count.warning {
                color: #f59e0b !important;
            }

            .char-count.error {
                color: #ef4444 !important;
            }

            .validation-message {
                font-size: 13px !important;
                font-weight: 500 !important;
                display: none !important;
                align-items: center !important;
                gap: 6px !important;
            }

            .validation-message.error {
                color: #ef4444 !important;
                display: flex !important;
            }

            .validation-message.success {
                color: #10b981 !important;
                display: flex !important;
            }

            .validation-message.warning {
                color: #f59e0b !important;
                display: flex !important;
            }

            .rate-limit-warning {
                background: rgba(245, 158, 11, 0.1) !important;
                border: 1px solid rgba(245, 158, 11, 0.3) !important;
                border-radius: 12px !important;
                padding: 16px !important;
                margin-bottom: 24px !important;
                color: #f59e0b !important;
                display: flex !important;
                align-items: center !important;
                gap: 12px !important;
            }

            .button-group {
                display: flex !important;
                gap: 12px !important;
                margin-top: 32px !important;
            }

            .btn-primary {
                flex: 2 !important;
                background: linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%) !important;
                color: white !important;
                border: none !important;
                padding: 16px 24px !important;
                border-radius: 12px !important;
                font-size: 16px !important;
                font-weight: 700 !important;
                cursor: pointer !important;
                transition: all 0.3s ease !important;
                box-shadow: 0 8px 25px rgba(0, 212, 255, 0.3) !important;
            }

            .btn-primary:hover:not(:disabled) {
                transform: translateY(-2px) !important;
                box-shadow: 0 12px 35px rgba(0, 212, 255, 0.4) !important;
            }

            .btn-primary:disabled {
                opacity: 0.5 !important;
                cursor: not-allowed !important;
                transform: none !important;
            }

            .btn-secondary {
                flex: 1 !important;
                background: rgba(255, 255, 255, 0.1) !important;
                color: white !important;
                border: 2px solid rgba(255, 255, 255, 0.2) !important;
                padding: 14px 20px !important;
                border-radius: 10px !important;
                font-size: 14px !important;
                font-weight: 600 !important;
                cursor: pointer !important;
                transition: all 0.3s ease !important;
            }

            .btn-secondary:hover {
                background: rgba(255, 255, 255, 0.15) !important;
                border-color: rgba(255, 255, 255, 0.3) !important;
                transform: translateY(-1px) !important;
            }

            @keyframes spin {
                to { transform: rotate(360deg); }
            }
        `;
        
        document.head.appendChild(style);
    }

    setupEventListeners() {
        const updateBtn = document.getElementById('updateBtn');
        
        // Setup validation for all inputs
        ['newDisplayName', 'professionalTitle', 'department'].forEach(fieldId => {
            const input = document.getElementById(fieldId);
            if (input) {
                input.addEventListener('input', () => this.validateField(fieldId));
                input.addEventListener('blur', () => this.validateField(fieldId));
                // Initial validation
                this.validateField(fieldId);
            }
        });
        
        // Setup update button
        if (updateBtn) {
            updateBtn.addEventListener('click', () => this.executeUpdate());
        }
        
        // Close on escape
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && document.getElementById(this.dialogId)) {
                this.hide();
            }
        });
    }

    validateField(fieldId) {
        const input = document.getElementById(fieldId);
        const value = input.value;
        const rules = this.validationRules.displayName; // Use same rules for all fields
        
        // Character count update
        const charCountId = fieldId.replace(/([A-Z])/g, (match) => match.toLowerCase()) + 'CharCount';
        const charCountEl = document.getElementById(charCountId);
        if (charCountEl) {
            const limit = fieldId === 'newDisplayName' ? 50 : 100;
            charCountEl.textContent = `${value.length} / ${limit}`;
            charCountEl.className = 'char-count';
            if (value.length > limit * 0.9) charCountEl.classList.add('warning');
            if (value.length > limit) charCountEl.classList.add('error');
        }
        
        // Validation for display name only
        if (fieldId === 'newDisplayName') {
            const validation = this.validateDisplayName(value);
            const validationEl = document.getElementById('nameValidation');
            
            if (validationEl) {
                validationEl.textContent = validation.message;
                validationEl.className = `validation-message ${validation.type}`;
            }
            
            // Update input styling
            input.classList.remove('error', 'success');
            if (!validation.valid && validation.type === 'error') {
                input.classList.add('error');
            } else if (validation.valid) {
                input.classList.add('success');
            }
        }
        
        this.updateButtonState();
    }

    validateField(fieldId) {
        const input = document.getElementById(fieldId);
        if (!input) return { valid: true, message: '', type: 'success' };
        
        const value = input.value;
        const fieldName = fieldId === 'newDisplayName' ? 'displayName' : fieldId;
        const rules = this.validationRules[fieldName];
        
        // Update character count
        this.updateCharacterCount(fieldId, value);
        
        // Perform validation
        let validation;
        if (fieldId === 'newDisplayName') {
            validation = this.validateDisplayName(value);
        } else {
            validation = this.validateOptionalField(value, rules);
        }
        
        // Update UI
        this.updateValidationUI(fieldId, validation);
        this.updateButtonState();
        
        return validation;
    }
    
    validateDisplayName(name) {
        const rules = this.validationRules.displayName;
        
        // Required field check
        if (!name.trim()) {
            return { valid: false, message: 'Display name is required', type: 'error' };
        }
        
        // Length validation
        if (name.length < rules.minLength) {
            return { valid: false, message: `Name must be at least ${rules.minLength} characters`, type: 'error' };
        }
        
        if (name.length > rules.maxLength) {
            return { valid: false, message: `Name must be less than ${rules.maxLength} characters`, type: 'error' };
        }
        
        // Pattern validation
        if (!rules.pattern.test(name)) {
            return { valid: false, message: rules.message, type: 'error' };
        }
        
        // Enterprise content filtering
        const lowerName = name.toLowerCase();
        const hasInappropriate = this.inappropriateWords.some(word => 
            lowerName.includes(word) || lowerName === word
        );
        
        if (hasInappropriate) {
            return { valid: false, message: 'Please use your professional name', type: 'error' };
        }
        
        // Check for changes
        if (name === this.originalValues.displayName) {
            return { valid: false, message: 'No changes detected', type: 'warning' };
        }
        
        // Check for potential duplicates (simplified for demo)
        if (name.toLowerCase() === 'john smith' || name.toLowerCase() === 'jane doe') {
            return { valid: false, message: 'This name may already be in use', type: 'warning' };
        }
        
        return { valid: true, message: 'Name is valid and available', type: 'success' };
    }
    
    validateOptionalField(value, rules) {
        if (!value.trim()) {
            return { valid: true, message: '', type: 'success' };
        }
        
        // Length validation
        if (value.length > rules.maxLength) {
            return { valid: false, message: `Must be less than ${rules.maxLength} characters`, type: 'error' };
        }
        
        // Pattern validation
        if (!rules.pattern.test(value)) {
            return { valid: false, message: rules.message, type: 'error' };
        }
        
        return { valid: true, message: '', type: 'success' };
    }
    
    updateValidationUI(fieldId, validation) {
        const validationId = this.getValidationId(fieldId);
        const validationEl = document.getElementById(validationId);
        const input = document.getElementById(fieldId);
        
        if (validationEl) {
            validationEl.textContent = validation.message;
            validationEl.className = `validation-message ${validation.type}`;
            validationEl.style.display = validation.message ? 'flex' : 'none';
        }
        
        if (input) {
            input.classList.remove('error', 'success');
            if (!validation.valid && validation.type === 'error') {
                input.classList.add('error');
                input.setAttribute('aria-invalid', 'true');
            } else if (validation.valid && validation.message) {
                input.classList.add('success');
                input.setAttribute('aria-invalid', 'false');
            }
        }
    }
    
    getValidationId(fieldId) {
        const mapping = {
            'newDisplayName': 'nameValidation',
            'professionalTitle': 'titleValidation',
            'department': 'deptValidation'
        };
        return mapping[fieldId] || fieldId + 'Validation';
    }

    updateButtonState() {
        const displayName = document.getElementById('newDisplayName')?.value || '';
        const validation = this.validateDisplayName(displayName);
        const updateBtn = document.getElementById('updateBtn');
        
        if (updateBtn) {
            updateBtn.disabled = !validation.valid || validation.type === 'warning';
        }
    }

    async executeUpdate() {
        const newName = document.getElementById('newDisplayName')?.value?.trim();
        const newTitle = document.getElementById('professionalTitle')?.value?.trim();
        const newDepartment = document.getElementById('department')?.value?.trim();
        
        // Enterprise validation
        const nameValidation = this.validateDisplayName(newName);
        if (!nameValidation.valid) {
            this.showErrorNotification('Please correct validation errors before updating');
            return;
        }
        
        // Rate limit enforcement
        if (this.rateLimitData?.isLimited) {
            this.showErrorNotification('Update restricted: Please wait 24 hours between updates');
            return;
        }
        
        const updateBtn = document.getElementById('updateBtn');
        updateBtn.innerHTML = '<div style="display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,0.3); border-radius: 50%; border-top-color: white; animation: spin 1s ease-in-out infinite; margin-right: 8px;"></div>Updating...';
        updateBtn.disabled = true;
        
        try {
            // Create audit trail entry
            await this.createAuditTrail({
                oldValues: this.originalValues,
                newValues: { displayName: newName, professionalTitle: newTitle, department: newDepartment },
                changeDescription: this.generateChangeDescription(this.originalValues, { displayName: newName, professionalTitle: newTitle, department: newDepartment })
            });
            
            // Update Firebase with comprehensive data
            await this.updateFirebaseProfile({
                displayName: newName,
                professionalTitle: newTitle,
                department: newDepartment,
                lastIdentityUpdate: firebase.firestore.FieldValue.serverTimestamp(),
                lastUpdated: firebase.firestore.FieldValue.serverTimestamp()
            });
            
            // Update display name using existing service
            await this.profileScene.updateDisplayName(newName);
            
            // Clear draft
            this.clearDraft();
            
            // Close dialog
            this.hide();
            
            // Show success notification
            this.showSuccessNotification('Identity matrix updated successfully! Changes applied across all systems.');
            
            // Invalidate cache to refresh data
            if (window.userDataCacheService) {
                window.userDataCacheService.clearCache('identity-update');
            }
            
        } catch (error) {
            console.error('Error updating identity:', error);
            
            // Restore button state
            updateBtn.innerHTML = 'Update Profile';
            updateBtn.disabled = false;
            
            // Show specific error message
            if (error.code === 'permission-denied') {
                this.showErrorNotification('Permission denied: Unable to update profile');
            } else if (error.code === 'failed-precondition') {
                this.showErrorNotification('Update failed: Please try again in a moment');
            } else {
                this.showErrorNotification('Update failed: Please check your connection and try again');
            }
        }
    }
    
    async createAuditTrail(changeData) {
        const user = auth.currentUser;
        if (!user) return;
        
        try {
            await db.collection('identityAudit').add({
                userId: user.uid,
                userEmail: user.email,
                oldValues: changeData.oldValues,
                newValues: changeData.newValues,
                changeDescription: changeData.changeDescription,
                timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                ipAddress: 'hidden', // Would be captured server-side in production
                userAgent: navigator.userAgent.substring(0, 100), // Truncated for storage
                sessionId: this.generateSessionId()
            });
            
            console.log('📊 Identity audit trail created');
        } catch (error) {
            console.warn('Warning: Audit trail creation failed:', error);
            // Don't block the update if audit fails
        }
    }
    
    generateChangeDescription(oldValues, newValues) {
        const changes = [];
        
        if (oldValues.displayName !== newValues.displayName) {
            changes.push(`Display name changed from "${oldValues.displayName}" to "${newValues.displayName}"`);
        }
        
        if (oldValues.professionalTitle !== newValues.professionalTitle) {
            changes.push(`Professional title changed from "${oldValues.professionalTitle || 'None'}" to "${newValues.professionalTitle || 'None'}"`);
        }
        
        if (oldValues.department !== newValues.department) {
            changes.push(`Department changed from "${oldValues.department || 'None'}" to "${newValues.department || 'None'}"`);
        }
        
        return changes.join('; ') || 'No changes detected';
    }
    
    async updateFirebaseProfile(updateData) {
        const user = auth.currentUser;
        if (!user) throw new Error('User not authenticated');
        
        try {
            await db.collection('userProfiles').doc(user.uid).set(updateData, { merge: true });
            console.log('📊 Firebase profile updated with comprehensive data');
        } catch (error) {
            console.error('Firebase profile update error:', error);
            throw error;
        }
    }
    
    generateSessionId() {
        return Date.now().toString(36) + Math.random().toString(36).substring(2);
    }
    
    showErrorNotification(message) {
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 30px;
            right: 30px;
            background: rgba(239, 68, 68, 0.9);
            color: white;
            padding: 16px 24px;
            border-radius: 12px;
            font-weight: 600;
            z-index: 70000;
            transform: translateX(400px);
            transition: transform 0.4s ease;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        `;
        notification.textContent = '❌ ' + message;
        
        document.body.appendChild(notification);
        
        requestAnimationFrame(() => {
            notification.style.transform = 'translateX(0)';
        });
        
        setTimeout(() => {
            notification.style.transform = 'translateX(400px)';
            setTimeout(() => notification.remove(), 400);
        }, 4000);
    }

    showSuccessNotification(message) {
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            top: 30px;
            right: 30px;
            background: rgba(16, 185, 129, 0.9);
            color: white;
            padding: 16px 24px;
            border-radius: 12px;
            font-weight: 600;
            z-index: 70000;
            transform: translateX(400px);
            transition: transform 0.4s ease;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        `;
        notification.textContent = '✅ ' + message;
        
        document.body.appendChild(notification);
        
        requestAnimationFrame(() => {
            notification.style.transform = 'translateX(0)';
        });
        
        setTimeout(() => {
            notification.style.transform = 'translateX(400px)';
            setTimeout(() => notification.remove(), 400);
        }, 3000);
    }
}
